import {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type FocusEvent as ReactFocusEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
} from "react";
import styled, { type DefaultTheme } from "styled-components";

import { dataVizKeyOf, isDataVizToneName } from "../../theme/dataVizPalette";
import { Tooltip, useTooltipController } from "../Tooltip";
import { controlFocusRing, controlTransition } from "../foundations/controlStyles";
import {
  formatMediaPlaybackTime,
  MEDIA_TIMELINE_POLICY,
  resolveMediaTimelineSelectionEdgeBounds,
} from "./mediaTimelineMath";
import type {
  MediaTimelineSelectionRange,
  MediaTimelineSelectionTone,
  TimelineDragTarget,
  TimelineSelectionEdge,
} from "./timelineTypes";

export const SELECTION_DRAG_TARGET: Record<
  TimelineSelectionEdge,
  Extract<TimelineDragTarget, "selectionStart" | "selectionEnd">
> = {
  start: "selectionStart",
  end: "selectionEnd",
};

export function resolveSelectionDragEdge(target: TimelineDragTarget): TimelineSelectionEdge | null {
  if (target === "selectionStart") return "start";
  if (target === "selectionEnd") return "end";
  return null;
}

const VALUE_PRECISION = 1e6;
const roundValue = (seconds: number) => Math.round(seconds * VALUE_PRECISION) / VALUE_PRECISION;

/**
 * The span is the only layer the seat adds: one flat alpha tint, so the duration hatch beneath
 * reads through unchanged. A striped tint of its own would beat against that hatch (different
 * period and angle) and alias unevenly at 1x, so the texture is left to the base. Media outside
 * the span keeps the bare hatch, and the tail past the duration stays plain surface — three
 * zones told apart by the span alone, as SigmaKit's original did.
 */
export interface SelectionPalette {
  fill: string;
  line: string;
  lineHover: string;
  lineActive: string;
}

// The band carries no text, so it can sit well above the list-row selection tint (`bg.selected`,
// ~20%): this lands near the ~50% the SigmaKit original painted its trim stripes with.
const BAND_FILL_PERCENT = 45;

/** Hue a span paints when the consumer names none — the bar's only default of its own. */
export const DEFAULT_MEDIA_TIMELINE_SPAN_TONE: MediaTimelineSelectionTone = "dataViz.blue";

/**
 * One formula for every tone: the categorical dataViz hue as a solid base, mixed toward
 * transparent for the band tint and, for the handle's hover/active steps, toward the ink (the
 * emphasis direction that reads on both themes; Toast/NotificationBadge tint their tones the
 * same way). Segment bands read the same palette, so a band and a selection in one hue cannot
 * drift apart.
 */
export function resolveSelectionPalette(
  theme: DefaultTheme,
  tone: MediaTimelineSelectionTone,
): SelectionPalette {
  const key = dataVizKeyOf(isDataVizToneName(tone) ? tone : DEFAULT_MEDIA_TIMELINE_SPAN_TONE);
  const base = theme.color.dataViz[key];
  const ink = theme.color.text.default;
  return {
    fill: `color-mix(in srgb, ${base} ${BAND_FILL_PERCENT}%, transparent)`,
    line: base,
    lineHover: `color-mix(in srgb, ${base} 85%, ${ink})`,
    lineActive: `color-mix(in srgb, ${base} 70%, ${ink})`,
  };
}

const SelectionRangeRegion = styled.div<{ $tone: MediaTimelineSelectionTone }>`
  position: absolute;
  z-index: 1;
  top: 0;
  right: calc(100% - var(--c6-media-selection-end));
  left: var(--c6-media-selection-start);
  height: 100%;
  /* No outline of its own: a solid tone line reads far louder than the tint, and the handles
     already mark the edges (the original drew none either). */
  background: ${({ theme, $tone }) => resolveSelectionPalette(theme, $tone).fill};
  pointer-events: none;
`;

// Rem so the hit area scales with the root font; the bar rescales the px policy the same way.
const HANDLE_HIT_WIDTH = `${
  MEDIA_TIMELINE_POLICY.selectionHandleHitWidthPx / MEDIA_TIMELINE_POLICY.rootFontBasisPx
}rem`;

/**
 * The anchor sits on the logical edge (the same percentage position the regions use) plus a
 * bar-computed pixel shift that spreads two overlapping handles, keeps a boundary handle whole
 * above viewport navigation, and holds the pair apart even when both clamp at one
 * boundary. The dragged handle is raised so it never drops behind its sibling mid-drag. Drags
 * are delta-based, so any visual displacement never feeds back into the value.
 */
const SelectionHandleAnchor = styled(Tooltip)<{ $edge: TimelineSelectionEdge; $raised: boolean }>`
  position: absolute;
  z-index: ${({ $raised }) => ($raised ? 8 : 7)};
  top: 0;
  left: ${({ $edge }) =>
    $edge === "start"
      ? "calc(var(--c6-media-selection-start) + var(--c6-media-selection-handle-shift-start, 0px))"
      : "calc(var(--c6-media-selection-end) + var(--c6-media-selection-handle-shift-end, 0px))"};
  display: inline-flex;
  width: ${HANDLE_HIT_WIDTH};
  height: 100%;
  align-items: center;
  justify-content: center;
  cursor: ew-resize;
  transform: translateX(-50%);
  /* The track opts out of touch panning, but a handle must keep its own pointer stream too. */
  touch-action: none;
`;

const SelectionHandleGrip = styled.div<{
  $dragging: boolean;
  $focusVisible: boolean;
  $tone: MediaTimelineSelectionTone;
}>`
  box-sizing: border-box;
  display: flex;
  width: 0.75rem;
  height: 60%;
  align-items: center;
  justify-content: center;
  /* Tone fill rather than the original's white pill: a tone-filled handle stays visible on the
     dark theme, where a surface-filled one sank into the hatch. The ring is the fill's own hue
     one lightness step down, defined against the fill (currentColor) rather than the theme, so
     its prominence does not swing between light and dark the way a surface or ink ring did —
     and it tracks the hover/active fill steps automatically. */
  border: 2px solid oklch(from currentColor calc(l - 0.16) c h);
  border-radius: ${({ theme }) => theme.shape.radius.control};
  background: currentColor;
  box-shadow: ${({ theme }) => theme.elevation.shadow.level[1]};
  color: ${({ theme, $dragging, $tone }) => {
    const palette = resolveSelectionPalette(theme, $tone);
    return $dragging ? palette.lineActive : palette.line;
  }};
  ${controlTransition("color", "background-color")}

  &::after {
    width: 2px;
    height: 45%;
    border-radius: 999px;
    background: ${({ theme }) => theme.color.text.onControl};
    content: "";
  }

  /* Hover reads on the whole hit area (the anchor), not just the drawn grip. */
  [data-media-selection-handle]:hover > &:not([aria-disabled="true"]) {
    color: ${({ theme, $dragging, $tone }) => {
      const palette = resolveSelectionPalette(theme, $tone);
      return $dragging ? palette.lineActive : palette.lineHover;
    }};
  }

  /* Ring only for keyboard-origin focus; same specificity as the reset so template order wins. */
  &:focus {
    ${({ $focusVisible }) => ($focusVisible ? controlFocusRing("2px") : "outline: none;")}
  }

  &[aria-disabled="true"] {
    /* Inert handles take the documented disabled pair outright: neutral fill, border.disabled
       ring and grip — not a lightness step derived from the fill. */
    border-color: ${({ theme }) => theme.color.border.disabled};
    color: ${({ theme }) => theme.color.bg.control.disabled};
    cursor: default;

    &::after {
      background: ${({ theme }) => theme.color.border.disabled};
    }
  }
`;

export type SelectionHandlePointerHandler = (
  edge: TimelineSelectionEdge,
  event: ReactPointerEvent<HTMLElement>,
) => void;

interface SelectionHandleProps {
  edge: TimelineSelectionEdge;
  range: MediaTimelineSelectionRange;
  durationSeconds: number;
  stepSeconds: number;
  label: string;
  disabled: boolean;
  dragging: boolean;
  tone: MediaTimelineSelectionTone;
  onPointerDown: SelectionHandlePointerHandler;
  onEdgeCandidate: SelectionEdgeCandidateHandler;
  onFocusChange: (edge: TimelineSelectionEdge, focused: boolean) => void;
  /** Called with the unmounting grip while it still owns focus; the owner picks a live target. */
  onFocusRescue: (source: HTMLElement) => void;
}

/** `keyboard` marks a focus-scoped edit, which the owner keeps inside the viewport. */
export type SelectionEdgeCandidateHandler = (
  edge: TimelineSelectionEdge,
  candidateSeconds: number,
  keyboard: boolean,
) => void;

function SelectionHandle({
  edge,
  range,
  durationSeconds,
  stepSeconds,
  label,
  disabled,
  dragging,
  tone,
  onPointerDown,
  onEdgeCandidate,
  onFocusChange,
  onFocusRescue,
}: SelectionHandleProps) {
  const gripRef = useRef<HTMLDivElement>(null);
  const focusRescueRef = useRef(onFocusRescue);
  focusRescueRef.current = onFocusRescue;
  const focusChangeRef = useRef(onFocusChange);
  focusChangeRef.current = onFocusChange;
  // A handle can unmount while it owns focus (zoomed/panned out of the viewport, editing
  // withdrawn). The HTML focus fixup would drop focus to <body>; hand it to the timeline slider
  // instead. Layout-effect cleanup runs before React detaches the node, so activeElement still
  // identifies it.
  useLayoutEffect(
    () => () => {
      const grip = gripRef.current;
      if (grip === null) return;
      // No blur reaches React for a removed node, so the owner's focus bookkeeping is closed here.
      focusChangeRef.current(edge, false);
      if (document.activeElement === grip) focusRescueRef.current(grip);
    },
    [edge],
  );
  const tooltip = useTooltipController();
  const { show, hide } = tooltip;
  // Same focus modality as the timeline slider: a press hands the handle focus without a ring,
  // and the first navigation key brings the ring back for keyboard users. The latch is consumed
  // by the focus event (or cleared by blur/keys), never reset right after `focus()`.
  const pointerFocusRef = useRef(false);
  const [focusVisible, setFocusVisible] = useState(false);
  const value = edge === "start" ? range.startSeconds : range.endSeconds;
  const valueText = formatMediaPlaybackTime(value, durationSeconds);
  // The same grid-aware bounds the resolver enforces, so the advertised range is reachable.
  const bounds = resolveMediaTimelineSelectionEdgeBounds(range, durationSeconds, stepSeconds);
  // The readout is the same text hover/focus show; a drag keeps it pinned while the handle moves
  // (the changing content re-anchors the bubble) and drops it on release like the original.
  useEffect(() => {
    if (dragging) show();
    else hide();
  }, [dragging, hide, show]);

  const handleKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (disabled) return;
    let candidate: number;
    switch (event.key) {
      case "ArrowLeft":
      case "ArrowDown":
        candidate = value - stepSeconds;
        break;
      case "ArrowRight":
      case "ArrowUp":
        candidate = value + stepSeconds;
        break;
      // Both handles share the media bounds as their Home/End targets; the resolver's gap
      // clamp turns those into "as far as the other edge allows".
      case "Home":
        candidate = 0;
        break;
      case "End":
        candidate = durationSeconds;
        break;
      default:
        return;
    }
    // Consumed here so a page-level shortcut scope never sees a handle's arrow keys.
    event.preventDefault();
    event.stopPropagation();
    pointerFocusRef.current = false;
    setFocusVisible(true);
    onEdgeCandidate(edge, candidate, true);
  };
  const handleFocus = (event: ReactFocusEvent<HTMLDivElement>) => {
    setFocusVisible(!pointerFocusRef.current);
    pointerFocusRef.current = false;
    onFocusChange(edge, true);
    tooltip.triggerProps.onFocus(event);
  };
  const handleBlur = () => {
    pointerFocusRef.current = false;
    setFocusVisible(false);
    onFocusChange(edge, false);
    tooltip.triggerProps.onBlur();
  };

  return (
    <SelectionHandleAnchor
      $edge={edge}
      $raised={dragging}
      content={valueText}
      placement="top"
      controller={tooltip}
      describeTrigger={false}
      data-media-selection-handle={edge}
      onMouseEnter={tooltip.triggerProps.onMouseEnter}
      onMouseLeave={tooltip.triggerProps.onMouseLeave}
      // The whole declared hit area starts the edit — a press on the anchor's margin must never
      // fall through to the track as a seek. Stops the track's own handler for that reason.
      onPointerDown={(event) => {
        if (disabled || event.button !== 0) return;
        event.stopPropagation();
        event.preventDefault();
        pointerFocusRef.current = true;
        gripRef.current?.focus({ preventScroll: true });
        onPointerDown(edge, event);
      }}
    >
      <SelectionHandleGrip
        ref={gripRef}
        $tone={tone}
        onFocus={handleFocus}
        onBlur={handleBlur}
        $dragging={dragging}
        $focusVisible={focusVisible}
        role="slider"
        tabIndex={disabled ? -1 : 0}
        aria-label={label}
        aria-orientation="horizontal"
        // The bounds always bracket the current value: a collapsed consumer range (start ===
        // end) would otherwise put valuenow outside [valuemin, valuemax]. The resolver restores
        // the one-step gap on the first edit.
        aria-valuemin={edge === "start" ? 0 : roundValue(Math.min(value, bounds.endMinSeconds))}
        aria-valuemax={
          edge === "start" ? roundValue(Math.max(value, bounds.startMaxSeconds)) : durationSeconds
        }
        aria-valuenow={value}
        aria-valuetext={valueText}
        aria-disabled={disabled ? "true" : undefined}
        data-media-selection-grip={edge}
        data-dragging={dragging ? "true" : undefined}
        onKeyDown={handleKeyDown}
      />
    </SelectionHandleAnchor>
  );
}

interface TimelineSelectionProps {
  /** A series band's existing fill owns the span; it needs only the shared handles. */
  showRegion?: boolean;
  range: MediaTimelineSelectionRange;
  durationSeconds: number;
  viewportStart: number;
  viewportDuration: number;
  stepSeconds: number;
  /** Handles mount only for an editable range; a disabled bar keeps them inert in place. */
  editable: boolean;
  disabled: boolean;
  dragTarget: TimelineDragTarget;
  tone: MediaTimelineSelectionTone;
  labels: { start: string; end: string };
  onHandlePointerDown: SelectionHandlePointerHandler;
  onEdgeCandidate: SelectionEdgeCandidateHandler;
  onFocusRescue: (source: HTMLElement) => void;
}

export const TimelineSelection = memo(function TimelineSelection({
  showRegion = true,
  range,
  durationSeconds,
  viewportStart,
  viewportDuration,
  stepSeconds,
  editable,
  disabled,
  dragTarget,
  tone,
  labels,
  onHandlePointerDown,
  onEdgeCandidate,
  onFocusRescue,
}: TimelineSelectionProps) {
  const visibleEnd = viewportStart + viewportDuration;
  const inViewport = (seconds: number) => seconds >= viewportStart && seconds <= visibleEnd;
  const draggingEdge = resolveSelectionDragEdge(dragTarget);
  // The focused handle is exempt from the viewport rule: it stays mounted (clamped at the track
  // edge) so keyboard focus survives a zoom/pan or an edit the consumer has not applied yet.
  const [focusedEdge, setFocusedEdge] = useState<TimelineSelectionEdge | null>(null);
  const handleFocusChange = useCallback((edge: TimelineSelectionEdge, focused: boolean) => {
    setFocusedEdge((previous) => (focused ? edge : previous === edge ? null : previous));
  }, []);

  return (
    <>
      {showRegion ? (
        <SelectionRangeRegion
          $tone={tone}
          data-media-selection-range="true"
          data-media-selection-tone={tone}
        />
      ) : null}
      {editable
        ? (["start", "end"] as const).map((edge) => {
            const seconds = edge === "start" ? range.startSeconds : range.endSeconds;
            // Same viewport rule as the playhead: an off-viewport handle is not drawn — except
            // the focused one. Applied keyboard edits pan the viewport along (owner); a focused
            // handle that still unmounts (editing withdrawn) rescues focus (above).
            if (!inViewport(seconds) && focusedEdge !== edge) return null;
            return (
              <SelectionHandle
                key={edge}
                edge={edge}
                range={range}
                durationSeconds={durationSeconds}
                stepSeconds={stepSeconds}
                label={labels[edge]}
                tone={tone}
                disabled={disabled}
                dragging={draggingEdge === edge}
                onPointerDown={onHandlePointerDown}
                onEdgeCandidate={onEdgeCandidate}
                onFocusChange={handleFocusChange}
                onFocusRescue={onFocusRescue}
              />
            );
          })
        : null}
    </>
  );
});
